import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User, LoginCredentials } from '@/services/authService';
import { excelTokenStore } from '@/services/api';
import { extractErrorMessage } from '@/utils/fetchErrors';
import { saveRefreshToken, loadRefreshToken, clearRefreshToken } from '@/utils/tokenStorage';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // On mount, try to restore session from persisted refresh token
  useEffect(() => {
    restoreSession();
  }, []);

  const restoreSession = async () => {
    try {
      const storedRefreshToken = await loadRefreshToken();
      if (!storedRefreshToken) {
        setIsLoading(false);
        return;
      }
      const { accessToken, refreshToken, user: userData } = await authService.excelRefreshToken(storedRefreshToken);
      excelTokenStore.accessToken = accessToken;
      excelTokenStore.refreshToken = refreshToken;
      await saveRefreshToken(refreshToken);
      setUser(userData);
    } catch {
      // Stored refresh token is expired or invalid — clear it and show login
      await clearRefreshToken();
      excelTokenStore.clear();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      setError(null);
      setIsLoading(true);
      const { accessToken, refreshToken, user: userData } = await authService.excelLogin(credentials);
      excelTokenStore.accessToken = accessToken;
      excelTokenStore.refreshToken = refreshToken;
      await saveRefreshToken(refreshToken);
      setUser(userData);
    } catch (err) {
      setUser(null);
      excelTokenStore.clear();
      setError(extractErrorMessage(err));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await clearRefreshToken();
    excelTokenStore.clear();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
